import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardHeader, Container } from 'reactstrap';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PndApi from '../../../../utils/pnd/PndRestApi';
import LinearProgress from '@mui/material/LinearProgress';
import { Field, reduxForm } from 'redux-form';
import renderSelectField from 'src/shared/components/form/Select';
import { connect } from 'react-redux';
import { IconButton, CircularProgress, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { ERROR_MESSAGES } from 'src/utils/pnd/PndConstants';
import { formatDate } from 'src/utils/DateUtils';
import { showNotification } from 'src/shared/components/Notification';
import MobileDeviceCancellationLogDetails from './components/MobileDeviceCancellationLogDetails';

const MobileDeviceCancellationLogIndex = ({ reset, change, formValues }) => {
  useEffect(() => {
    loadFilterOptions();
  }, []);

  const [reactTableData, setReactTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isNoRecordFound, setNoRecordFound] = useState(false);
  const [riderIds, setRiderIds] = useState([]);
  const [customerIds, setCustomerIds] = useState([]);

  const columns = useMemo(
    () => [
      {
        Header: '#',
        accessor: (row, i) => i + 1,
        disableGlobalFilter: true,
        disableSortBy: true,
        width: 5,
        Cell: ({ row }) => row.index + 1,
      },
      {
        Header: 'Customer Name',
        accessor: 'customerName',
        disableGlobalFilter: false,
      },
      {
        Header: 'Driver Name',
        accessor: 'driverName',
        disableGlobalFilter: false,
      },
      {
        Header: 'Device ID',
        accessor: 'device_id',
        disableGlobalFilter: true,
      },
      {
        Header: 'Cancelled Date Time',
        accessor: 'cancelledDateTime',
        Cell: ({ value }) => value ? formatDate(value) : '',
        disableGlobalFilter: true,
      },
      // {
      //   Header: 'Cancelled By',
      //   accessor: 'cancelledBy',
      //   disableGlobalFilter: false,
      // },
    ],
    [],
  );

  const loadFilterOptions = async () => {
    try {
      setLoading(true);
      // Load riders
      const ridersResponse = await PndApi.GetAllRidersDetails();
      if (ridersResponse.status === 200 && ridersResponse.data.status === 200) {
        const ridersList = [{ label: 'All Drivers', value: 0 }];
        ridersResponse.data.data.forEach(item => {
          ridersList.push({ label: item.riderName, value: item.driverId });
        });
        setRiderIds(ridersList);
      }

      // Load customers (clients)
      const customersResponse = await PndApi.GetAllInformapClientDetails();
      if (customersResponse.status === 200 && customersResponse.data.status === 200) {
        const customersList = [{ label: 'All Customers', value: 0 }];
        customersResponse.data.data.forEach(item => {
          customersList.push({ label: item.clientName, value: item.customerId });
        });
        setCustomerIds(customersList);
      }
    } catch (error) {
      console.error('Error loading filter options:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerChange = async (selectedCustomer) => {
    const customerId = selectedCustomer?.value || 0;
    
    // Clear and reset the selected Driver field
    change('riderId', { label: 'All Drivers', value: 0 });

    try {
      setLoading(true);
    
        // Load drivers filtered by customerId
        const response = await PndApi.GetAllDriverDetailsByCustomerId(customerId);
        if (response.status === 200 && response.data.status === 200) {
          const ridersList = [{ label: 'All Drivers', value: 0 }];
          const driversData = response.data.data || [];
          driversData.forEach(item => {
            ridersList.push({ label: item.driverName+"-"+item?.userName, value: item.riderId });
          });
          setRiderIds(ridersList);
        } else if (response.status === 200 && response.data.status === 204) {
          setRiderIds([{ label: 'All Drivers', value: 0 }]);
        }
      
    } catch (error) {
      console.error('Error fetching drivers by customer ID:', error);
      setRiderIds([{ label: 'All Drivers', value: 0 }]);
    } finally {
      setLoading(false);
    }
  };

  const searchBasedOnFilters = async () => {
    // If no values selected, default to 0
    const rId = formValues?.riderId?.value !== undefined ? formValues.riderId.value : 0;
    const cId = formValues?.customerId?.value !== undefined ? formValues.customerId.value : 0;

    if (!cId || cId === 0) {
      showNotification(
        "theme-light",
        "ltr",
        "Validation Error",
        "Please select a Customer Name."
      );
      return;
    }

    try {
      setLoading(true);
      setNoRecordFound(false);
      setReactTableData(null);

      const response = await PndApi.GetLicenseCancellationLog(rId, cId);
      if (response.status === 200 && response.data.status === 200) {
        const rowsData = response.data.data || [];
        if (rowsData.length > 0) {
          setReactTableData({ tableHeaderData: columns, tableRowsData: rowsData });
          setNoRecordFound(false);
        } else {
          setNoRecordFound(true);
          setReactTableData({ tableHeaderData: columns, tableRowsData: null });
        }
      } else if (response.status === 200 && response.data.status === 204) {
        setNoRecordFound(true);
        setReactTableData({ tableHeaderData: columns, tableRowsData: null });
      } else {
        showNotification(
          "theme-light",
          "ltr",
          `ErrorCode: ${response.data.status}`,
          `Message:${response?.data?.message || ERROR_MESSAGES.tryCatchError}`,
        );
      }
    } catch (error) {
      console.error("searchBasedOnFilters -->", error);
      showNotification(
        "theme-light",
        "ltr",
        `ErrorCode: 500`,
        ERROR_MESSAGES.tryCatchError,
      );
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    reset();
    setReactTableData(null);
    setNoRecordFound(false);
    loadFilterOptions();
  };

  return (
    <Container fluid className="px-2 py-2">
      <Card className="border-0 shadow-sm mb-3">
        <CardHeader className="bg-white py-2 border-bottom-0">
          <h6 className="mb-0 fw-bold text-primary">Device License Log</h6>
        </CardHeader>
        <CardBody className="p-3">
          <form className="form" onSubmit={(e) => e.preventDefault()}>
            <div className="d-flex align-items-end flex-wrap gap-3">
              <div style={{ width: '280px' }}>
                <label className="form-label fw-bold text-secondary mb-1" style={{ fontSize: '0.92rem' }}>
                  Customer Name <span className="text-danger">*</span>
                </label>
                <Field
                  name="customerId"
                  component={renderSelectField}
                  options={customerIds}
                  placeholder="Select Customer Name"
                  menuPortalTarget={document.body}
                  onChange={handleCustomerChange}
                />
              </div>
              <div style={{ width: '280px' }}>
                <label className="form-label fw-bold text-secondary mb-1" style={{ fontSize: '0.92rem' }}>Driver Name</label>
                <Field
                  name="riderId"
                  component={renderSelectField}
                  options={riderIds}
                  placeholder="Select Driver Name"
                  menuPortalTarget={document.body}
                />
              </div>
              <div className="d-flex gap-2 mb-1">
                <Tooltip title="Clear Filters" arrow>
                  <IconButton
                    onClick={clearFilters}
                    size="small"
                    sx={{
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #dee2e6',
                      '&:hover': { backgroundColor: '#e2e6ea' }
                    }}
                  >
                    <RestartAltIcon fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Search Logs" arrow>
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={searchBasedOnFilters}
                    disabled={loading}
                    sx={{
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': { backgroundColor: 'primary.dark' },
                      '&.Mui-disabled': { backgroundColor: '#ccc' }
                    }}
                  >
                    {loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </div>
            </div>
            <div className="mt-2 text-muted" style={{ fontSize: '0.82rem' }}>
              <span className="text-danger">*</span> Note: Please select a Customer Name to retrieve the Device License Cancellation Logs.
            </div>
          </form>
        </CardBody>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardBody className="p-2">
          {(reactTableData && reactTableData.tableRowsData != null) &&
            <MobileDeviceCancellationLogDetails reactTableData={reactTableData} />}
          {loading && <LinearProgress color="primary" />}
          {isNoRecordFound && <MobileDeviceCancellationLogDetails reactTableData={reactTableData} isNoRecordFound={isNoRecordFound} />}
        </CardBody>
      </Card>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  formValues: state.form.MobileDeviceCancellationLogForm ? state.form.MobileDeviceCancellationLogForm.values : {},
});

export default connect(mapStateToProps)(reduxForm({ form: 'MobileDeviceCancellationLogForm', enableReinitialize: true })(MobileDeviceCancellationLogIndex));
